"use client";

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Eye, EyeOff } from "lucide-react";
import { useState, useEffect } from "react";
import { extractBasicFromAggregate, fetchUserAggregate } from "@/lib/user";
import { fetchWithAuth } from "@/lib/http";
import logger from "@/lib/logger";
import useClientTranslation from "@/hooks/useClientTranslation";

export default function ProfilePage() {
  const router = useRouter();
  const { t } = useClientTranslation("accountInfo");
  const [openPassword, setOpenPassword] = useState(false);
  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const [editedUsername, setEditedUsername] = useState("张小明");
  const [isSavingUsername, setIsSavingUsername] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [phoneVerificationSent, setPhoneVerificationSent] = useState(false);
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [isSavingPhone, setIsSavingPhone] = useState(false);
  const [codeCountdown, setCodeCountdown] = useState(0);
  const [availableModels, setAvailableModels] = useState<unknown[]>([]);
  const [loadingModels, setLoadingModels] = useState(true);
  const [loadingUser, setLoadingUser] = useState(true);
  const [userEmail, setUserEmail] = useState<string>("");
  const [balance, setBalance] = useState<number>(0);
  const [userGroupName, setUserGroupName] = useState<string>("");
  const [userGroupDescription, setUserGroupDescription] = useState<string>("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [affCode, setAffCode] = useState<string>("");
  const [affRate, setAffRate] = useState<number | null>(null);
  const [affEarned, setAffEarned] = useState<number | null>(null);
  const [rewardTotal, setRewardTotal] = useState<number | null>(null);
  const [loadingRewardTotal, setLoadingRewardTotal] = useState(false);

  // 个人信息页头像首字母（使用已提取的昵称/用户名）
  const avatarInitials = (() => {
    try {
      const s = (editedUsername || "").trim();
      if (!s) return "__";
      return s.slice(0, 2).toUpperCase();
    } catch {
      return "__";
    }
  })();

  const modelsApiEndpoint =
    process.env.NEXT_PUBLIC_API_MODELS_ENDPOINT || "/api/user/models";

  const handleEditUsername = () => {
    setIsEditingUsername(true);
  };

  const handleSaveUsername = async () => {
    try {
      setIsSavingUsername(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      setIsEditingUsername(false);
      toast.success(t("messages.usernameSaved"));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      logger.error("保存用户名失败: %s", msg);
      toast.error(t("messages.saveFailed"));
    } finally {
      setIsSavingUsername(false);
    }
  };

  const handleSendPhoneCode = async () => {
    // if (!phoneNumber.trim()) {
    //   toast.error("请输入手机号码");
    //   return;
    // }
    // if (!/^1[3-9]\d{9}$/.test(phoneNumber)) {
    //   toast.error("请输入有效的手机号码");
    //   return;
    // }

    try {
      setIsSendingCode(true);
      // 移除调试输出：发送验证码
      await new Promise(resolve => setTimeout(resolve, 1000));
      setPhoneVerificationSent(true);
      setCodeCountdown(60);
      toast.success(t("messages.codeSent"));

      const interval = setInterval(() => {
        setCodeCountdown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      logger.error("发送验证码失败: %s", msg);
      // 错误通过 toast 提示，无需控制台输出
      toast.error(t("messages.sendFailed"));
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleVerifyPhoneCode = async () => {
    if (!verificationCode.trim()) {
      toast.error(t("messages.enterCode"));
      return;
    }

    try {
      setIsVerifyingCode(true);
      // 移除调试输出：验证码
      await new Promise(resolve => setTimeout(resolve, 1000));
      setIsPhoneVerified(true);
      toast.success(t("messages.verifySuccess"));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      logger.error("验证验证码失败: %s", msg);
      // 错误通过 toast 提示，无需控制台输出
      toast.error(t("messages.verifyFailed"));
    } finally {
      setIsVerifyingCode(false);
    }
  };

  const handleSavePhone = async () => {
    try {
      setIsSavingPhone(true);
      // 移除调试输出：保存手机号
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success(t("messages.phoneSaved"));
      setShowPhoneModal(false);
      setPhoneNumber("");
      setVerificationCode("");
      setPhoneVerificationSent(false);
      setIsPhoneVerified(false);
      setCodeCountdown(0);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      logger.error("保存手机号失败: %s", msg);
      toast.error(t("messages.saveFailed"));
    } finally {
      setIsSavingPhone(false);
    }
  };

  const handleClosePhoneModal = () => {
    setShowPhoneModal(false);
    setPhoneNumber("");
    setVerificationCode("");
    setPhoneVerificationSent(false);
    setIsPhoneVerified(false);
    setCodeCountdown(0);
  };

  /**
   * 封装修改密码的 API 调用（若无后端，仍会抛错以触发上层处理）
   */
  const changePassword = async (current: string, next: string) => {
    const res = await fetchWithAuth("/api/auth/change-password", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      // 后端期望字段为 old_password，因此这里使用 old_password 以兼容上游
      body: JSON.stringify({ old_password: current, new_password: next }),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      const msg =
        data && (data.message || data.error || (data as any).detail || JSON.stringify(data));
      throw new Error(msg || "修改密码失败");
    }

    // 返回解析后的数据（如果需要可以调整）
    return await res.json().catch(() => ({}));
  };

  /**
   * 修改密码处理函数
   */
  const handleChangePassword = async () => {
    // logger.debug("点击了确认修改按钮");
    // 验证输入
    if (!currentPassword.trim()) {
      toast.error(t("messages.enterCurrentPassword"));
      return;
    }
    if (!newPassword.trim()) {
      toast.error(t("messages.enterNewPassword"));
      return;
    }
    if (!confirmPassword.trim()) {
      toast.error(t("messages.confirmNewPassword"));
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(t("messages.passwordsNotMatch"));
      return;
    }
    if (newPassword === currentPassword) {
      toast.error(t("messages.passwordSameAsOld"));
      return;
    }
    if (newPassword.length < 6) {
      toast.error(t("messages.passwordTooShort"));
      return;
    }

    try {
      setIsChangingPassword(true);
      await changePassword(currentPassword, newPassword);
      logger.info("密码修改成功");
      toast.success(t("messages.passwordChanged"));

      // 重置表单和状态
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setOpenPassword(false);
    } catch (err: unknown) {
      if (err instanceof Error) {
        logger.error("密码修改失败:", err);
        toast.error(err.message || t("messages.passwordChangeFailed"));
      } else {
        logger.error("密码修改失败:", String(err));
        toast.error(t("messages.passwordChangeFailed"));
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  // 获取当前登录用户的基本信息（昵称/邮箱等）
  useEffect(() => {
    const loadUser = async () => {
      try {
        setLoadingUser(true);
        const data = await fetchUserAggregate([
          "basic",
          "profile",
          "info",
          "balance",
          "group",
          "appkeys",
          "onmodels",
        ]);

        const basic = extractBasicFromAggregate(data);

        const toStr = (v: unknown) =>
          typeof v === "string" ? v : typeof v === "number" ? String(v) : "";

        const nicknameCandidate =
          basic.nickname ??
          (basic as Record<string, unknown>)["real_name"] ??
          (basic as Record<string, unknown>)["name"];
        const nickname = toStr(nicknameCandidate);
        const email = toStr(basic.email ?? "");

        if (nickname) setEditedUsername(nickname);
        setUserEmail(email);

        // 提取余额数据（兼容数值/字符串/对象）
        const balanceRaw = (data as Record<string, unknown>)["balance"];
        let balanceValue = 0;

        if (balanceRaw !== undefined && balanceRaw !== null) {
          if (typeof balanceRaw === "number") {
            balanceValue = balanceRaw;
          } else if (typeof balanceRaw === "string") {
            balanceValue = parseFloat(balanceRaw) || 0;
          } else if (typeof balanceRaw === "object") {
            const b = balanceRaw as Record<string, unknown>;
            // 如果后端把 aff_code 放在 balance 对象里，这里提取它
            const possibleAff = b["aff_code"] ?? b["affCode"] ?? b["affid"];
            if (possibleAff) setAffCode(String(possibleAff));

            const candidate =
              (typeof b["balance"] === "number" ? b["balance"] : b["balance"]) ||
              (typeof b["amount"] === "number" ? b["amount"] : b["amount"]) ||
              (typeof b["value"] === "number" ? b["value"] : b["value"]) ||
              (typeof b["total"] === "number" ? b["total"] : b["total"]) ||
              (typeof b["available"] === "number" ? b["available"] : b["available"]);
            if (typeof candidate === "number") balanceValue = candidate;
            else if (typeof candidate === "string") balanceValue = parseFloat(candidate) || 0;
          }
        }

        setBalance(balanceValue);

        // 如果聚合数据中没有提供余额，或未获取到 aff_code，则向后端一次性请求 /api/topup/balance
        // 以避免为余额和邀请码发起重复请求。
        const hasBalanceFromAggregate = balanceRaw !== undefined && balanceRaw !== null;
        if ((!hasBalanceFromAggregate || !affCode) && typeof window !== "undefined") {
          try {
            const resp = await fetchWithAuth("/api/topup/balance", {
              method: "GET",
              headers: { "Content-Type": "application/json" },
            });
            if (resp && resp.ok) {
              const tb = await resp.json().catch(() => null);
              if (tb) {
                const q = tb.quota ?? tb.balance ?? null;
                if (typeof q === "number") setBalance(q);
                else if (typeof q === "string") setBalance(parseFloat(q) || 0);

                const possibleAff = tb["aff_code"] ?? tb["affCode"] ?? tb["affid"];
                if (possibleAff) setAffCode(String(possibleAff));
              }
            }
          } catch (e) {
            // 忽略后端回退失败
          }
        }

        // 提取分组名称与描述（兼容对象或数组返回）
        const groupData = (data as Record<string, unknown>)["group"];
        let groupName = "";
        let groupDesc = "";

        const pickGroupFields = (g: unknown) => {
          if (!g || typeof g !== "object") return;
          const gg = g as Record<string, unknown>;
          groupName =
            toStr(gg["name"]) ||
            toStr(gg["group_name"]) ||
            toStr(gg["groupName"]) ||
            toStr(gg["title"]) ||
            toStr(gg["label"]) ||
            groupName;
          groupDesc =
            toStr(gg["description"]) ||
            toStr(gg["desc"]) ||
            toStr(gg["remark"]) ||
            toStr(gg["note"]) ||
            groupDesc;
        };

        if (Array.isArray(groupData) && groupData.length > 0) {
          pickGroupFields(groupData[0]);
        } else if (groupData && typeof groupData === "object") {
          pickGroupFields(groupData);
        }

        setUserGroupName(groupName);
        setUserGroupDescription(groupDesc);

        // 提取可用 AI 模型数据（只保留对象项）
        const models = (data as Record<string, unknown>)["onmodels"];
        if (Array.isArray(models)) {
          const safeModels = models.filter(m => m && typeof m === "object") as Record<
            string,
            unknown
          >[];
          setAvailableModels(safeModels as unknown[]);
        } else {
          setAvailableModels([]);
        }

        // 如果后端把 aff_code 放在返回的根对象中，也从这里提取（用于兼容）
        try {
          const root = data as Record<string, unknown>;
          const rootAff = root["aff_code"] ?? root["affCode"] ?? root["affid"];
          if (rootAff) setAffCode(String(rootAff));
        } catch (e) {
          // ignore
        }

      } catch (err: unknown) {
        logger.debug("loadUser 失败:", err);
      } finally {
        setLoadingUser(false);
      }

      // 已从 fetchUserAggregate 返回的数据中提取 aff_code，如果未包含可在此处回退请求（目前回退被移除以避免重复）
    };

    loadUser();
  }, []);

  // 获取推广返利数据
  useEffect(() => {
    const loadAffiliate = async () => {
      try {
        const resp = await fetchWithAuth("/api/user/affiliate", {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });
        if (resp && resp.ok) {
          const data = await resp.json().catch(() => null);
          if (data) {
            // 邀请码（兼容 data.data / data.aff_code 等多种返回格式）
            const code = data.data ?? data.aff_code ?? data.affCode ?? data.affid;
            if (code) setAffCode(String(code));

            // 返利比例
            const rate = data.inviter_quota_rate ?? data.aff_rate ?? data.rate ?? data.commission_rate;
            if (rate !== undefined && rate !== null) {
              setAffRate(Number(rate));
            }
            // 已返利额度
            const earned = data.aff_history_quota ?? data.earned ?? data.total_earned ?? data.history_quota;
            if (earned !== undefined && earned !== null) {
              setAffEarned(Number(earned));
            }
          }
        }
      } catch (e) {
        logger.debug("获取推广数据失败:", e);
      }
    };
    loadAffiliate();
  }, []);

  // 获取返利总额（依赖 affCode）
  useEffect(() => {
    if (!affCode) return;
    const loadRewardTotal = async () => {
      try {
        setLoadingRewardTotal(true);
        const resp = await fetchWithAuth(
          `/api/user/reward-total?aff_code=${encodeURIComponent(affCode)}`,
          { method: "GET", headers: { "Content-Type": "application/json" } },
        );
        if (resp && resp.ok) {
          const json = await resp.json().catch(() => null);
          if (json && json.success && json.data) {
            setRewardTotal(Number(json.data.total_quota ?? 0));
          }
        }
      } catch (e) {
        logger.debug("获取返利总额失败:", e);
      } finally {
        setLoadingRewardTotal(false);
      }
    };
    loadRewardTotal();
  }, [affCode]);

  return (
    <div className="w-full min-h-screen p-3 flex justify-center">
      <div className="w-full space-y-6">
        <h1 className="text-3xl mb-6 text-foreground font-bold flex">{t("title")}</h1>
        <Card className="rounded-xl shadow-sm py-0 bg-card">
          <CardContent className="py-2 space-y-4">
            <div className="flex items-center gap-4">
              {/* 替换为黑色背景的圆形头像，显示用户名前两个大写字符 */}
              <div className="w-12 h-12 rounded-full bg-black flex items-center justify-center">
                <span className="text-white font-bold text-base leading-none">
                  {avatarInitials}
                </span>
              </div>
              <div className=" ">
                <h2 className="text-lg text-foreground">
                  {isEditingUsername ? (
                    <Input
                      type="text"
                      value={editedUsername}
                      onChange={e => setEditedUsername(e.target.value)}
                      className="h-8 text-sm"
                      autoFocus
                    />
                  ) : (
                    editedUsername
                  )}
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {loadingUser
                    ? t("loading")
                    : userGroupDescription || userGroupName || t("profile.advancedDeveloper")}
                </p>
              </div>
              {/* <Button
                variant="default"
                className="bg-blue-500 text-white hover:bg-blue-600 text-xs px-3 py-1 h-7 cursor-pointer "
                onClick={() => {
                  if (isEditingUsername) {
                    handleSaveUsername();
                  } else {
                    handleEditUsername();
                  }
                }}
                disabled={isSavingUsername}
              >
                {isEditingUsername ? (isSavingUsername ? t("profile.saving") : t("profile.confirm")) : t("profile.editUsername")}
              </Button> */}
              <div className="mt-2 flex items-center gap-2"></div>
            </div>
            <div className="text-xs mt-1.5">
              <div className="space-y-0.5">
                <p className="text-muted-foreground">{t("fields.email")}</p>
                <p className="text-foreground">{loadingUser ? t("loading") : userEmail || "-"}</p>
              </div>
            </div>

            <Separator className="my-3" />

            <div>
              <span className="text-lg font-bold text-foreground">{t("promotion.title")}</span>
              <div className="flex items-center mt-2 gap-2">
                <div className="flex flex-wrap items-center gap-3 w-full">
                  <span className="text-sm text-secondary-foreground flex items-center">
                    {t("promotion.inviteCode")}:{" "}
                    <span className="font-mono text-base text-foreground ml-1">
                      {affCode || t("loading")}
                    </span>
                  </span>
                  <Button
                    size="sm"
                    className="flex items-center text-sm cursor-pointer"
                    onClick={async () => {
                      const text = affCode || "";
                      if (!text) {
                        toast.error(t("promotion.codeUnavailable"));
                        return;
                      }
                      try {
                        if (
                          typeof navigator !== "undefined" &&
                          navigator.clipboard &&
                          navigator.clipboard.writeText
                        ) {
                          await navigator.clipboard.writeText(text);
                          toast.success(t("promotion.copySuccess"));
                          return;
                        }
                        const ta = document.createElement("textarea");
                        ta.value = text;
                        ta.style.position = "fixed";
                        ta.style.left = "-9999px";
                        document.body.appendChild(ta);
                        ta.select();
                        const ok = document.execCommand("copy");
                        document.body.removeChild(ta);
                        if (ok) toast.success(t("promotion.copySuccess"));
                        else throw new Error("execCommand copy failed");
                      } catch (e) {
                        logger.error("复制邀请码失败:", e);
                        toast.error(t("promotion.copyFailed"));
                      }
                    }}
                  >
                    {t("promotion.copy")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex items-center text-sm cursor-pointer"
                    onClick={async () => {
                      if (!affCode) {
                        toast.error(t("promotion.codeUnavailable"));
                        return;
                      }
                      const link = `${window.location.origin}/register?affcode=${affCode}`;
                      try {
                        if (navigator?.clipboard?.writeText) {
                          await navigator.clipboard.writeText(link);
                          toast.success(t("promotion.copyLinkSuccess"));
                          return;
                        }
                        const ta = document.createElement("textarea");
                        ta.value = link;
                        ta.style.position = "fixed";
                        ta.style.left = "-9999px";
                        document.body.appendChild(ta);
                        ta.select();
                        const ok = document.execCommand("copy");
                        document.body.removeChild(ta);
                        if (ok) toast.success(t("promotion.copyLinkSuccess"));
                        else throw new Error("execCommand copy failed");
                      } catch (e) {
                        logger.error("复制推广链接失败:", e);
                        toast.error(t("promotion.copyLinkFailed"));
                      }
                    }}
                  >
                    {t("promotion.copyLink")}
                  </Button>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-4 mt-3">
                <span className="text-sm text-secondary-foreground flex items-center">
                  {t("promotion.rebateRate")}:{" "}
                  <span className="font-bold text-primary ml-1">
                    {affRate !== null ? `${(affRate * 100).toFixed(0)}%` : "10%"}
                  </span>
                </span>
                <span className="text-sm text-secondary-foreground flex items-center">
                  {t("promotion.rebateEarned")}:{" "}
                  <span className="font-bold text-green-600 dark:text-green-400 ml-1">
                    {loadingRewardTotal
                      ? t("loading")
                      : rewardTotal !== null
                        ? `$${(rewardTotal / 500000).toFixed(2)}`
                        : affEarned !== null
                          ? `$${(affEarned / 500000).toFixed(2)}`
                          : "$0.00"}
                  </span>
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl shadow-sm py-1 bg-card">
          <CardContent className="py-2">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-foreground">{t("balance.title")}</h3>
              <Button
                className="rounded-xl px-6"
                onClick={() => router.push("/pim/recharge")}
              >
                {t("balance.recharge")}
              </Button>
            </div>

            <div className="flex flex-col items-center gap-2 py-8">
              <div className="w-full flex flex-col md:flex-row items-stretch justify-center gap-6">
                <div className="flex-1 text-center">
                  <p className="text-sm text-muted-foreground mb-2">{t("balance.currentCNY")}</p>
                  <p className="text-4xl font-bold text-primary">
                    {loadingUser ? t("loading") : `￥${((balance / 500000) * 7).toFixed(2)}`}
                  </p>
                </div>

                <div className="flex-1 text-center">
                  <p className="text-sm text-muted-foreground mb-2">{t("balance.currentUSD")}</p>
                  <p className="text-4xl font-bold text-primary">
                    {loadingUser ? t("loading") : `$${(balance / 500000).toFixed(2)}`}
                  </p>
                </div>
              </div>

              <p className="text-xs text-muted-foreground mt-2">{t("balance.exchangeRate")}</p>
            </div>

            <Separator className="my-6" />

            <div>
              <div className="mt-6 flex justify-center">
                <Button
                  variant="outline"
                  className="rounded-xl px-6 border-primary text-primary hover:bg-primary/10 cursor-pointer w-full"
                  onClick={() => {
                    router.push("/pim/recharge#rechargeRecords");
                  }}
                >
                  {t("balance.viewAllRecords")}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        {/* 
        <Card id="availableModels" className="rounded-2xl shadow-sm bg-card pt-10">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-6 text-foreground">{t("availableModels.title")}</h3>
            {loadingUser ? (
              <div className="flex justify-center items-center py-12">
                <div className="text-muted-foreground">{t("loading")}</div>
              </div>
            ) : availableModels.length > 0 ? (
              <div className="flex flex-wrap gap-3">
                {availableModels.map((model: unknown, index: number) => {
                  const m = model as Record<string, unknown>;
                  const modelId = typeof m["model_id"] === "string" ? m["model_id"] : String(index);
                  const modelName =
                    typeof m["model_name"] === "string"
                      ? (m["model_name"] as string)
                      : typeof m["name"] === "string"
                      ? (m["name"] as string)
                      : String(m["model_id"] ?? m["name"] ?? index);

                  return (
                    <div
                      key={modelId}
                      className="px-4 py-2 border border-primary/30 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors cursor-pointer text-sm font-medium"
                    >
                      {modelName}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex justify-center items-center py-12">
                <div className="text-muted-foreground">{t("availableModels.noModels")}</div>
              </div>
            )}
          </CardContent>
        </Card> */}

        <Card className="rounded-2xl shadow-sm bg-card">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-4 text-foreground">{t("settings.title")}</h3>

            {/* 安全设置 */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-secondary-foreground mb-3">{t("settings.security")}</h4>
              <div className="space-y-2">
                {/* 修改密码 */}
                <div className="border border-border rounded-lg">
                  <Button
                    variant="ghost"
                    className="w-full flex justify-between items-center p-3 hover:bg-secondary text-foreground rounded-none"
                    onClick={() => setOpenPassword(!openPassword)}
                  >
                    <span className="text-sm font-medium">{t("settings.changePassword")}</span>
                    {openPassword ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </Button>
                  {openPassword && (
                    <div className="px-3 py-3 space-y-3 border-t border-border">
                      <div className="relative">
                        <Input
                          type={showCurrentPassword ? "text" : "password"}
                          placeholder={t("settings.currentPassword")}
                          value={currentPassword}
                          onChange={e => setCurrentPassword(e.target.value)}
                          disabled={isChangingPassword}
                          className="pr-10"
                        />
                        <div
                          className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-muted-foreground"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        >
                          {showCurrentPassword ? (
                            <Eye className="w-4 h-4" />
                          ) : (
                            <EyeOff className="w-4 h-4" />
                          )}
                        </div>
                      </div>
                      <div className="relative">
                        <Input
                          type={showNewPassword ? "text" : "password"}
                          placeholder={t("settings.newPassword")}
                          value={newPassword}
                          onChange={e => setNewPassword(e.target.value)}
                          disabled={isChangingPassword}
                          className="pr-10"
                        />
                        <div
                          className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-muted-foreground"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                        >
                          {showNewPassword ? (
                            <Eye className="w-4 h-4" />
                          ) : (
                            <EyeOff className="w-4 h-4" />
                          )}
                        </div>
                      </div>
                      <div className="relative">
                        <Input
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder={t("settings.confirmPassword")}
                          value={confirmPassword}
                          onChange={e => setConfirmPassword(e.target.value)}
                          disabled={isChangingPassword}
                          className="pr-10"
                        />
                        <div
                          className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-muted-foreground"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? (
                            <Eye className="w-4 h-4" />
                          ) : (
                            <EyeOff className="w-4 h-4" />
                          )}
                        </div>
                      </div>
                      <Button
                        className="w-full"
                        onClick={handleChangePassword}
                        disabled={isChangingPassword}
                      >
                        {isChangingPassword ? t("settings.changing") : t("settings.confirmChange")}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 修改手机号弹窗 */}
      <Dialog open={showPhoneModal} onOpenChange={(open) => { if (!open) handleClosePhoneModal(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("phoneModal.title")}</DialogTitle>
            <DialogDescription>{t("phoneModal.phoneLabel")}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">{t("phoneModal.phoneLabel")}</label>
              <Input
                type="text"
                placeholder={t("phoneModal.phonePlaceholder")}
                value={phoneNumber}
                onChange={e => setPhoneNumber(e.target.value)}
                disabled={phoneVerificationSent}
              />
            </div>

            {phoneVerificationSent && (
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">{t("phoneModal.codeLabel")}</label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder={t("phoneModal.codePlaceholder")}
                    value={verificationCode}
                    onChange={e => setVerificationCode(e.target.value)}
                    disabled={isPhoneVerified}
                    maxLength={6}
                    className="flex-1"
                  />
                  <Button
                    variant="secondary"
                    disabled={codeCountdown > 0}
                    onClick={handleSendPhoneCode}
                    className="text-xs whitespace-nowrap"
                  >
                    {codeCountdown > 0 ? t("phoneModal.resendCountdown", { seconds: codeCountdown }) : t("phoneModal.resend")}
                  </Button>
                </div>
              </div>
            )}

            {isPhoneVerified && (
              <div className="px-3 py-2 bg-green-50 dark:bg-green-900/20 border border-green-500 rounded-lg text-green-700 dark:text-green-300 text-sm">
                ✓ {t("phoneModal.verifySuccess")}
              </div>
            )}
          </div>

          <DialogFooter className="flex gap-3 sm:gap-3">
            <Button
              variant="outline"
              onClick={handleClosePhoneModal}
              disabled={isSavingPhone}
            >
              {t("phoneModal.cancel")}
            </Button>
            {!phoneVerificationSent ? (
              <Button
                onClick={handleSendPhoneCode}
                disabled={isSendingCode || !phoneNumber.trim()}
              >
                {isSendingCode ? t("phoneModal.sending") : t("phoneModal.sendCode")}
              </Button>
            ) : !isPhoneVerified ? (
              <Button
                onClick={handleVerifyPhoneCode}
                disabled={isVerifyingCode || !verificationCode.trim()}
              >
                {isVerifyingCode ? t("phoneModal.verifying") : t("phoneModal.verify")}
              </Button>
            ) : (
              <Button
                onClick={handleSavePhone}
                disabled={isSavingPhone}
              >
                {isSavingPhone ? t("phoneModal.savingPhone") : t("phoneModal.confirm")}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
